import { useEffect, useCallback } from "react";
import { type ApolloError } from "@apollo/client";
import { useGetCountryCodesQuery } from "#graphql/supergraphHooks";
import { type GetCountryCodesQuery } from "#graphql/supergraphTypes";
import { clientContext } from "#utils/graphql_utils";
import { type DetailedPaymentPrefUnionObjectType } from "../common/types";
import { useConsolidatedPaymentPrefContext } from "../Context";
import { type SelectedBankCountry } from "../Context/types";
import {
  findCountryByAlpha2Code,
  findCountryById,
  findCountryByName,
  getCountryFromPaymentPrefDetail,
} from "./utils/set-bank-country-utils";

export type UseSetBankCountryArgs = {
  paymentPrefDetail?: DetailedPaymentPrefUnionObjectType | null;
  countryCode?: string;
  countryId?: string;
};

export type UseSetBankCountryReturn = {
  loading: boolean;
  error?: ApolloError;
  countryData?: GetCountryCodesQuery;
  resolveAndSet: (
    args: UseSetBankCountryArgs & { countryName?: string },
  ) => SelectedBankCountry | undefined;
};

/**
 * A unified hook to resolve and set the bank country in the consolidated payment preference context.
 *
 * It handles three resolution paths in priority order:
 * 1. From an existing payment preference detail (edit flow hydration).
 * 2. From an alpha2 country code (fixed-country rails like ACH, BECS, etc.).
 * 3. From a country Relay ID (on-submit resolution).
 *
 * It also returns an imperative `resolveAndSet` function for manual resolution during form submission.
 */
export const useSetBankCountry = ({
  paymentPrefDetail,
  countryCode,
  countryId,
}: UseSetBankCountryArgs = {}): UseSetBankCountryReturn => {
  const { selectedBankCountry, setSelectedBankCountry, setBankCountryLocked } =
    useConsolidatedPaymentPrefContext();

  const {
    data: countryData,
    loading,
    error,
  } = useGetCountryCodesQuery({ context: clientContext() });

  const resolveAndSet = useCallback(
    ({
      paymentPrefDetail: detail,
      countryCode: code,
      countryId: id,
      countryName: name,
    }: UseSetBankCountryArgs & { countryName?: string }) => {
      // If we already have a country in context, we avoid overwriting it unless
      // this is a manual imperative call with new data.
      if (selectedBankCountry && !detail && !code && !id && !name) {
        return selectedBankCountry;
      }

      let resolved: SelectedBankCountry | undefined;

      // 1. Resolve from payment preference detail – returns SelectedBankCountry directly
      // so no secondary country-data lookup is needed.
      resolved = getCountryFromPaymentPrefDetail(detail ?? undefined);

      // 2. Resolve from Alpha2 code
      if (!resolved && code) {
        resolved = findCountryByAlpha2Code(countryData, code);
      }

      // 3. Resolve from country ID
      if (!resolved && id) {
        resolved = findCountryById(countryData, id);
      }

      // 4. Resolve from country name (mailing address fallback)
      if (!resolved && name) {
        resolved = findCountryByName(countryData, name);
      }

      if (resolved) {
        setSelectedBankCountry(resolved);
      }

      return resolved;
    },
    [countryData, selectedBankCountry, setSelectedBankCountry],
  );

  useEffect(() => {
    // Only attempt auto-resolution once country data is loaded and context isn't already set.
    if (!loading && !selectedBankCountry && (paymentPrefDetail || countryCode || countryId)) {
      const resolved = resolveAndSet({ paymentPrefDetail, countryCode, countryId });
      // Lock the downstream country field only for fixed-rail flows (countryCode/countryId).
      // Don't lock for edit-hydration (paymentPrefDetail) so users can change the country when editing.
      if (resolved && (countryCode || countryId)) {
        setBankCountryLocked(true);
      }
    }
  }, [
    loading,
    paymentPrefDetail,
    countryCode,
    countryId,
    selectedBankCountry,
    resolveAndSet,
    setBankCountryLocked,
  ]);

  return {
    loading,
    error,
    countryData,
    resolveAndSet,
  };
};
